"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

/** 小圆内对勾（主色底 + 白勾，用于浅灰底 / 黑底卡） */
function CheckBulletPrimary({ children }: { children: ReactNode }) {
	return (
		<li className="flex items-start gap-2.5 text-sm md:text-base">
			<span
				className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-themePrimary400 text-white"
				aria-hidden
			>
				<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M1 5L4.5 8.5L11 1.5"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</span>
			<span>{children}</span>
		</li>
	);
}

/** 白底圆 + 主色勾（中间绿色卡） */
function CheckBulletInverted({ children }: { children: ReactNode }) {
	return (
		<li className="flex items-start gap-2.5 text-sm text-white md:text-base">
			<span
				className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-themePrimary400"
				aria-hidden
			>
				<svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M1 5L4.5 8.5L11 1.5"
						stroke="currentColor"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</span>
			<span>{children}</span>
		</li>
	);
}

export interface JSPrecisionSheetMetalSolutionsProps {
	/** 命名空间：`sheetMetalFabricationJSSolutions` */
	translationNamespace?: string;
	/** 三张配图，顺序：快速样件 / 小批量 / 大批量 */
	imageSrcs: readonly [string, string, string];
}

/**
 * JS Precision Sheet Metal Solutions：双行标题 + 绿线装饰，三列能力卡（灰 / 绿 / 黑）
 */
export default function JSPrecisionSheetMetalSolutions({
	translationNamespace = "sheetMetalFabricationJSSolutions",
	imageSrcs,
}: JSPrecisionSheetMetalSolutionsProps) {
	const t = useTranslations(translationNamespace);

	const cards = [
		{
			image: imageSrcs[0],
			imageAltKey: "card1ImageAlt" as const,
			titleKey: "card1Title" as const,
			descKey: "card1Desc" as const,
			qtyKey: "card1Quantity" as const,
			leadKey: "card1LeadTime" as const,
			variant: "light" as const,
		},
		{
			image: imageSrcs[1],
			imageAltKey: "card2ImageAlt" as const,
			titleKey: "card2Title" as const,
			descKey: "card2Desc" as const,
			qtyKey: "card2Quantity" as const,
			leadKey: "card2LeadTime" as const,
			variant: "green" as const,
		},
		{
			image: imageSrcs[2],
			imageAltKey: "card3ImageAlt" as const,
			titleKey: "card3Title" as const,
			descKey: "card3Desc" as const,
			qtyKey: "card3Quantity" as const,
			leadKey: "card3LeadTime" as const,
			variant: "dark" as const,
		},
	];

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题：与稿图一致 — 首行「JS Precision」+ 向右绿线，次行「Sheet Metal Solutions」居中 */}
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<div className="mx-auto flex max-w-xl flex-col items-stretch sm:max-w-2xl">
						{/* items-end：绿线与首行文字底边对齐；固定宽度，不再拉满 */}
						<div className="flex items-end justify-center gap-3 sm:gap-3.5">
							<span className="shrink-0 text-2xl font-bold leading-none text-black sm:text-[30px] md:text-4xl">
								{t("titleLine1")}
							</span>
							<div className="h-[3px] w-[160px] shrink-0 bg-themePrimary400 max-md:w-[50px]" aria-hidden />
						</div>
						<h2 className="mt-2 text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("titleLine2")}
						</h2>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5 lg:gap-6">
					{cards.map((card) => {
						const isGreen = card.variant === "green";
						const isDark = card.variant === "dark";
						const bgClass = isGreen ? "bg-themePrimary400" : isDark ? "bg-black" : "bg-[#e8e8e8]";
						const textClass = isGreen || isDark ? "text-white" : "text-black";
						const descClass = isGreen || isDark ? "text-white/95" : "text-black";

						return (
							<div key={card.titleKey} className={`flex flex-col overflow-hidden shadow-sm ${bgClass}`}>
								<div className="relative aspect-[4/3] w-full bg-black/5">
									<Image
										src={card.image}
										alt={t(card.imageAltKey)}
										fill
										className="object-cover p-4"
										sizes="(max-width: 768px) 100vw, 33vw"
									/>
								</div>
								<div className={`flex flex-1 flex-col p-5 sm:p-6 md:p-7 ${textClass}`}>
									<h3 className={`mb-3 text-lg font-bold sm:text-xl md:text-2xl ${textClass}`}>
										{t(card.titleKey)}
									</h3>
									<p className={`mb-6 flex-1 text-sm leading-relaxed md:text-base ${descClass}`}>
										{t(card.descKey)}
									</p>
									<ul className="mt-auto space-y-2.5">
										{isGreen ? (
											<>
												<CheckBulletInverted>{t(card.qtyKey)}</CheckBulletInverted>
												<CheckBulletInverted>{t(card.leadKey)}</CheckBulletInverted>
											</>
										) : (
											<>
												<CheckBulletPrimary>{t(card.qtyKey)}</CheckBulletPrimary>
												<CheckBulletPrimary>{t(card.leadKey)}</CheckBulletPrimary>
											</>
										)}
									</ul>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
